/**
 * Iter 186 — Shipping & Delivery settings page.
 * ============================================================================
 *
 * Removes the "Wave 2 · STUB" placeholder that was showing for the Shipping
 * card on /website-builder/settings. Full CRUD over shipping zones + rates +
 * local pickup + free-shipping threshold + processing days.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Truck, Package, Store, DollarSign, Clock, Save, Globe, Boxes, KeyRound, Eye, EyeOff } from 'lucide-react';
import { GlassCard, DashboardBackdrop } from '@/components/dashboard/dashboard-ui';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const nextId = (p) => `${p}_${Math.random().toString(36).slice(2, 8)}`;

const COUNTRIES = ['US', 'CA', 'MX', 'GB', 'AU', 'NZ', 'EU', 'ROW'];

/* ─────────────── Tier editor (weight or price) ─────────────── */
function TierEditor({ tiers, onChange, upToLabel, upToKey, testidPrefix }) {
  const rows = tiers.length ? tiers : [{ [upToKey]: 500, price: 5 }];
  const add = () => onChange([...rows, { [upToKey]: 0, price: 0 }]);
  const patchRow = (i, delta) => onChange(rows.map((r, idx) => idx === i ? { ...r, ...delta } : r));
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div className="pl-3 border-l-2 border-[#A78BFA]/30 space-y-1.5" data-testid={`${testidPrefix}-tier-editor`}>
      <div className="grid grid-cols-[1fr_100px_28px] gap-2 text-[10px] uppercase tracking-wider text-white/40">
        <span>{upToLabel}</span><span>Rate</span><span />
      </div>
      {rows.map((t, i) => (
        <div key={i} className="grid grid-cols-[1fr_100px_28px] gap-2 items-center">
          <input
            type="number" min="0" step={upToKey === 'up_to_price' ? '0.01' : '1'}
            value={t[upToKey] ?? ''}
            onChange={(e) => patchRow(i, { [upToKey]: Number(e.target.value) || 0 })}
            placeholder={upToKey === 'up_to_price' ? '$50' : '500'}
            className="px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs focus:border-[#A78BFA]/50 outline-none"
            data-testid={`${testidPrefix}-tier-upto-${i}`}
          />
          <div className="flex items-center gap-1">
            <span className="text-white/40 text-xs">$</span>
            <input
              type="number" min="0" step="0.01"
              value={t.price ?? 0}
              onChange={(e) => patchRow(i, { price: Number(e.target.value) || 0 })}
              className="flex-1 px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs focus:border-[#A78BFA]/50 outline-none"
              data-testid={`${testidPrefix}-tier-price-${i}`}
            />
          </div>
          <button onClick={() => remove(i)} className="w-6 h-6 rounded bg-red-500/10 hover:bg-red-500/20 grid place-items-center"><Trash2 size={10} className="text-red-400" /></button>
        </div>
      ))}
      <button onClick={add} className="text-[10px] font-semibold text-[#A78BFA] hover:text-white inline-flex items-center gap-1"><Plus size={10} /> Add tier</button>
      <p className="text-[10px] text-white/40">
        Set the last tier&apos;s &quot;up to&quot; to 0 to make it the catch-all for anything heavier / higher.
      </p>
    </div>
  );
}

/* ─────────────── Ship-from address ─────────────── */
function ShipFromCard({ ship_from, onChange }) {
  const set = (k, v) => onChange({ ...ship_from, [k]: v });
  const f = ship_from || {};
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#A78BFA] flex items-center gap-1.5"><Truck size={11} /> Ship-from address</p>
      </div>
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <input value={f.name || ''} onChange={e => set('name', e.target.value)} placeholder="Business or sender name" data-testid="ship-from-name" className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#A78BFA]/50 md:col-span-2" />
        <input value={f.street1 || ''} onChange={e => set('street1', e.target.value)} placeholder="Street address" data-testid="ship-from-street1" className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#A78BFA]/50 md:col-span-2" />
        <input value={f.city || ''} onChange={e => set('city', e.target.value)} placeholder="City" data-testid="ship-from-city" className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#A78BFA]/50" />
        <input value={f.state || ''} onChange={e => set('state', e.target.value)} placeholder="State / Province" data-testid="ship-from-state" className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#A78BFA]/50" />
        <input value={f.zip || ''} onChange={e => set('zip', e.target.value)} placeholder="Postal / ZIP" data-testid="ship-from-zip" className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#A78BFA]/50" />
        <input value={f.country || 'US'} onChange={e => set('country', e.target.value.toUpperCase())} placeholder="Country (US / CA / …)" data-testid="ship-from-country" className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#A78BFA]/50" />
        <input value={f.phone || ''} onChange={e => set('phone', e.target.value)} placeholder="Phone (required by some carriers)" data-testid="ship-from-phone" className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#A78BFA]/50 md:col-span-2" />
      </div>
    </div>
  );
}

/* ─────────────── Package presets ─────────────── */
function PackagesCard({ packages, onChange }) {
  const list = packages || [];
  const add = () => onChange([...list, { id: nextId('pkg'), name: 'New package', length: 25, width: 18, height: 5, empty_weight: 0, dimension_unit: 'cm', weight_unit: 'g' }]);
  const patchPkg = (i, delta) => onChange(list.map((p, idx) => idx === i ? { ...p, ...delta } : p));
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const setDefault = (i) => onChange(list.map((p, idx) => ({ ...p, default: idx === i })));
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#A78BFA] flex items-center gap-1.5"><Boxes size={11} /> Package presets</p>
        <button onClick={add} className="text-[11px] font-bold text-white bg-white/5 hover:bg-white/10 border border-white/10 px-2.5 py-1 rounded-md inline-flex items-center gap-1" data-testid="add-package"><Plus size={10} /> Add package</button>
      </div>
      <div className="space-y-2">
        {list.length === 0 && <p className="text-[11px] text-white/40 italic">No packages yet — add one so we have a fallback size when a product has no dimensions.</p>}
        {list.map((p, i) => (
          <div key={p.id || i} className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-3 grid grid-cols-[minmax(120px,1fr)_1fr_140px_120px_80px_auto] gap-2 items-center" data-testid={`package-row-${p.id || i}`}>
            <input value={p.name} onChange={(e) => patchPkg(i, { name: e.target.value })} placeholder="Small box" className="px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none focus:border-[#A78BFA]/50" />
            <div className="flex items-center gap-1">
              <input type="number" min="0" step="0.1" value={p.length} onChange={(e) => patchPkg(i, { length: Number(e.target.value) || 0 })} placeholder="L" className="w-14 px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none focus:border-[#A78BFA]/50" />
              <span className="text-white/30 text-xs">×</span>
              <input type="number" min="0" step="0.1" value={p.width}  onChange={(e) => patchPkg(i, { width: Number(e.target.value) || 0 })}  placeholder="W" className="w-14 px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none focus:border-[#A78BFA]/50" />
              <span className="text-white/30 text-xs">×</span>
              <input type="number" min="0" step="0.1" value={p.height} onChange={(e) => patchPkg(i, { height: Number(e.target.value) || 0 })} placeholder="H" className="w-14 px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none focus:border-[#A78BFA]/50" />
              <select value={p.dimension_unit || 'cm'} onChange={(e) => patchPkg(i, { dimension_unit: e.target.value })} className="w-14 px-1 py-1.5 rounded bg-white/5 border border-white/10 text-white text-[11px] outline-none focus:border-[#A78BFA]/50">
                <option value="cm">cm</option><option value="in">in</option>
              </select>
            </div>
            <div className="flex items-center gap-1">
              <input type="number" min="0" step="0.1" value={p.empty_weight ?? 0} onChange={(e) => patchPkg(i, { empty_weight: Number(e.target.value) || 0 })} placeholder="Weight" className="flex-1 px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs outline-none focus:border-[#A78BFA]/50" />
              <select value={p.weight_unit || 'g'} onChange={(e) => patchPkg(i, { weight_unit: e.target.value })} className="w-12 px-1 py-1.5 rounded bg-white/5 border border-white/10 text-white text-[11px] outline-none focus:border-[#A78BFA]/50">
                <option value="g">g</option><option value="kg">kg</option><option value="oz">oz</option><option value="lb">lb</option>
              </select>
            </div>
            <button
              onClick={() => setDefault(i)}
              className={`text-[10px] font-bold uppercase tracking-wider py-1 rounded ${p.default ? 'bg-[#A78BFA] text-[#0E0E23]' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
              data-testid={`package-default-${i}`}
            >
              {p.default ? '✓ Default' : 'Set default'}
            </button>
            <button onClick={() => remove(i)} className="w-7 h-7 rounded bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center"><Trash2 size={11} className="text-red-400" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────── Carrier account card ─────────────── */
const CARRIER_META = {
  easypost: {
    label: 'EasyPost',
    tagline: '100+ carriers behind one API (USPS · UPS · FedEx · DHL · Canada Post · Australia Post). Best for global reach.',
    docsUrl: 'https://www.easypost.com/account/api-keys',
    docsShort: 'easypost.com/account/api-keys',
    keyPlaceholder: 'EZAK…',
  },
  shippo: {
    label: 'Shippo',
    tagline: 'Simpler UX with the same major carriers. Great starter option; test keys start with `shippo_test_`.',
    docsUrl: 'https://apps.goshippo.com/settings/api',
    docsShort: 'apps.goshippo.com/settings/api',
    keyPlaceholder: 'shippo_live_…',
  },
};

function CarrierCard({ provider, meta, config, onSave, onDelete }) {
  const [apiKey, setApiKey] = useState('');
  const [reveal, setReveal] = useState(false);
  const [enabled, setEnabled] = useState(config?.enabled || false);
  const [testMode, setTestMode] = useState(config?.test_mode ?? true);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setEnabled(config?.enabled || false); setTestMode(config?.test_mode ?? true); }, [config?.enabled, config?.test_mode]);
  const submit = async () => {
    if (!apiKey && !config?.has_api_key) { toast.error('Enter an API key first'); return; }
    setSaving(true);
    try { await onSave(provider, { enabled, test_mode: testMode, api_key: apiKey || undefined }); setApiKey(''); toast.success(`${meta.label} saved`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-5" data-testid={`carrier-card-${provider}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-white font-bold flex items-center gap-2"><KeyRound size={14} className="text-[#A78BFA]" /> {meta.label}</h3>
          <p className="text-[11.5px] text-white/60 mt-1 leading-snug">{meta.tagline}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${config?.has_api_key ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/25' : 'bg-white/5 text-white/60 border-white/15'}`}>
          {config?.has_api_key ? 'Connected' : 'Not connected'}
        </span>
      </div>
      <div className="rounded-lg bg-[#A78BFA]/8 border border-[#A78BFA]/20 p-2.5 text-[11px] text-white/75">
        Grab your API {provider === 'shippo' ? 'token' : 'key'} from{' '}
        <a href={meta.docsUrl} target="_blank" rel="noreferrer" className="text-[#A78BFA] hover:text-white underline">{meta.docsShort}</a>. Keys are encrypted at rest.
      </div>
      <div className="mt-3">
        <div className="text-xs text-white/50 mb-1.5">API {provider === 'shippo' ? 'token' : 'key'}</div>
        <div className="relative">
          <input
            type={reveal ? 'text' : 'password'}
            value={apiKey} onChange={(e) => setApiKey(e.target.value)}
            placeholder={config?.has_api_key ? '•••••••• (saved — paste new to replace)' : meta.keyPlaceholder}
            className="w-full pr-9 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/25 outline-none focus:border-[#A78BFA]/50 font-mono"
            data-testid={`carrier-${provider}-key`}
          />
          <button type="button" onClick={() => setReveal(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center text-white/45 hover:text-white">
            {reveal ? <EyeOff size={13} /> : <Eye size={13} />}
          </button>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-4 flex-wrap">
        <label className="flex items-center gap-2 text-[12px] text-white/70">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid={`carrier-${provider}-enabled`} />
          Enable at checkout
        </label>
        <label className="flex items-center gap-2 text-[12px] text-white/70">
          <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} data-testid={`carrier-${provider}-testmode`} />
          Test mode
        </label>
        <div className="flex-1" />
        {config?.has_api_key && (
          <button onClick={() => onDelete(provider)} className="text-[11px] font-semibold text-rose-300 bg-rose-500/10 hover:bg-rose-500/15 px-3 py-1.5 rounded-lg" data-testid={`carrier-${provider}-disconnect`}>
            Disconnect
          </button>
        )}
        <button onClick={submit} disabled={saving} className="text-[11px] font-bold uppercase tracking-wider bg-[#A78BFA] text-[#0E0E23] hover:bg-white px-4 py-2 rounded-lg" data-testid={`carrier-${provider}-save`}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export default function SettingsShipping() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/builder/shipping`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setConfig(r.data))
      .catch(() => toast.error('Could not load shipping settings'))
      .finally(() => setLoading(false));
  }, []);

  const patch = (delta) => setConfig((c) => ({ ...c, ...delta }));
  const patchZones = (nextZones) => patch({ zones: nextZones });

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/builder/shipping`, config, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Shipping settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  // Iter 191n — carrier CRUD hits its own endpoint (never overwrites secrets via PUT /shipping)
  const saveCarrier = async (provider, payload) => {
    const { data } = await axios.put(`${API}/builder/shipping/carriers/${provider}`, payload, { headers: authHeaders() });
    setConfig((c) => ({ ...c, carriers: { ...(c?.carriers || {}), ...data } }));
  };
  const disconnectCarrier = async (provider) => {
    if (!(await themedConfirm(`Disconnect ${provider}?`))) return;
    await axios.delete(`${API}/builder/shipping/carriers/${provider}`, { headers: authHeaders() });
    setConfig((c) => ({
      ...c,
      carriers: { ...(c?.carriers || {}), [provider]: { enabled: false, test_mode: true, has_api_key: false } },
    }));
    toast.success('Disconnected');
  };

  if (loading) {
    return <div className="p-8 text-white/60" data-testid="shipping-loading">Loading shipping settings…</div>;
  }

  const zones = config?.zones || [];

  const addZone = () => patchZones([
    ...zones,
    { id: nextId('z'), name: 'New Zone', countries: [], rates: [] },
  ]);

  const updateZone = (zid, delta) => patchZones(zones.map(z => z.id === zid ? { ...z, ...delta } : z));
  const deleteZone = (zid) => patchZones(zones.filter(z => z.id !== zid));

  const addRate = (zid) => updateZone(zid, {
    rates: [...(zones.find(z => z.id === zid)?.rates || []),
      { id: nextId('r'), name: 'New rate', price: 5, min_days: 3, max_days: 7 }],
  });
  const updateRate = (zid, rid, delta) => {
    const zone = zones.find(z => z.id === zid);
    if (!zone) return;
    updateZone(zid, { rates: zone.rates.map(r => r.id === rid ? { ...r, ...delta } : r) });
  };
  const deleteRate = (zid, rid) => {
    const zone = zones.find(z => z.id === zid);
    if (!zone) return;
    updateZone(zid, { rates: zone.rates.filter(r => r.id !== rid) });
  };

  return (
    <div className="relative" data-testid="settings-shipping">
      <DashboardBackdrop />
      <div className="relative z-10 space-y-6 pb-12 max-w-4xl">
        <Link to="/website-builder/settings" data-testid="shipping-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white hover:text-[#A78BFA] font-semibold">
          <ArrowLeft size={12} /> Go Back
        </Link>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white font-syne tracking-tight">Shipping &amp; Delivery</h1>
            <p className="text-[#A5A8C9] text-sm mt-1.5">
              Set up zones, rates, local pickup, and free-shipping thresholds for your storefront.
            </p>
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-xs font-bold uppercase tracking-wider hover:shadow-[0_12px_30px_rgba(139,92,246,0.45)] transition-all inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="shipping-save"
          >
            <Save size={13} /> {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

        {/* General settings */}
        <GlassCard glow className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#A78BFA] mb-1.5 flex items-center gap-1.5"><DollarSign size={11} /> Free shipping over</p>
              <div className="flex items-center gap-2">
                <span className="text-white/60 text-sm">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={config?.free_shipping_over ?? ''}
                  onChange={(e) => patch({ free_shipping_over: e.target.value === '' ? null : Number(e.target.value) })}
                  placeholder="e.g. 50"
                  className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#A78BFA]/50 outline-none"
                  data-testid="shipping-free-over"
                />
              </div>
              <p className="text-[10px] text-white/40 mt-1">Leave blank to disable.</p>
            </label>

            <label className="block">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#A78BFA] mb-1.5 flex items-center gap-1.5"><Clock size={11} /> Default processing time (days)</p>
              <input
                type="number"
                min="0"
                max="30"
                value={config?.default_processing_days ?? 2}
                onChange={(e) => patch({ default_processing_days: Math.max(0, Number(e.target.value) || 0) })}
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#A78BFA]/50 outline-none"
                data-testid="shipping-processing-days"
              />
              <p className="text-[10px] text-white/40 mt-1">How long before you ship after purchase.</p>
            </label>
          </div>

          <div className="border-t border-white/5 pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!config?.local_pickup_enabled}
                onChange={(e) => patch({ local_pickup_enabled: e.target.checked })}
                className="mt-1 w-4 h-4 accent-[#A78BFA]"
                data-testid="shipping-pickup-enabled"
              />
              <div className="flex-1">
                <p className="text-sm text-white font-bold flex items-center gap-2"><Store size={13} className="text-[#A78BFA]" /> Offer local pickup</p>
                <p className="text-xs text-white/50 mt-0.5">Customers can pick up in person at markets, powwows, or your studio.</p>
              </div>
            </label>
            {config?.local_pickup_enabled && (
              <textarea
                value={config.local_pickup_notes || ''}
                onChange={(e) => patch({ local_pickup_notes: e.target.value })}
                placeholder="Pickup instructions (address, hours, etc.)"
                rows={2}
                className="mt-3 w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#A78BFA]/50 outline-none"
                data-testid="shipping-pickup-notes"
              />
            )}
          </div>
        </GlassCard>

        {/* Ship-from address */}
        <ShipFromCard ship_from={config?.ship_from} onChange={(v) => patch({ ship_from: v })} />

        {/* Package presets */}
        <PackagesCard packages={config?.packages} onChange={(v) => patch({ packages: v })} />

        {/* Shipping zones */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#A78BFA] flex items-center gap-1.5"><Globe size={11} /> Shipping zones</p>
              <p className="text-xs text-white/50 mt-1">Group destinations + rates. Customers see rates for their country at checkout.</p>
            </div>
            <button
              onClick={addZone}
              className="px-3 py-2 rounded-lg bg-[#A78BFA]/15 border border-[#A78BFA]/30 text-[#A78BFA] text-xs font-bold uppercase tracking-wider hover:bg-[#A78BFA]/25 transition-colors inline-flex items-center gap-1.5"
              data-testid="shipping-add-zone"
            >
              <Plus size={12} /> Add Zone
            </button>
          </div>

          {zones.length === 0 && (
            <GlassCard className="p-8 text-center">
              <Truck size={28} className="text-[#A78BFA] mx-auto mb-2" />
              <p className="text-sm text-white/70">No zones yet — add one to start charging shipping.</p>
            </GlassCard>
          )}

          <div className="space-y-4">
            {zones.map((z) => (
              <GlassCard key={z.id} className="p-5" data-testid={`shipping-zone-${z.id}`}>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex-1 min-w-0 space-y-2">
                    <input
                      value={z.name}
                      onChange={(e) => updateZone(z.id, { name: e.target.value })}
                      placeholder="Zone name (e.g. United States)"
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-bold focus:border-[#A78BFA]/50 outline-none"
                      data-testid={`shipping-zone-name-${z.id}`}
                    />
                    <div className="flex flex-wrap gap-1.5">
                      {COUNTRIES.map((c) => {
                        const on = (z.countries || []).includes(c);
                        return (
                          <button
                            key={c}
                            onClick={() => updateZone(z.id, {
                              countries: on ? z.countries.filter(x => x !== c) : [...(z.countries || []), c],
                            })}
                            className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-colors ${
                              on ? 'bg-[#A78BFA] text-[#0E0E23]' : 'bg-white/5 text-white/60 hover:bg-white/10'
                            }`}
                            data-testid={`shipping-zone-country-${z.id}-${c}`}
                          >
                            {c}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteZone(z.id)}
                    className="w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center shrink-0"
                    data-testid={`shipping-zone-delete-${z.id}`}
                  >
                    <Trash2 size={13} className="text-red-400" />
                  </button>
                </div>

                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 flex items-center gap-1.5"><Package size={10} /> Rates</p>
                  {(z.rates || []).map((r) => (
                    <div key={r.id} className="rounded-lg bg-white/[0.03] border border-white/[0.05] p-2 space-y-2" data-testid={`shipping-rate-${r.id}`}>
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <input
                          value={r.name}
                          onChange={(e) => updateRate(z.id, r.id, { name: e.target.value })}
                          placeholder="Name (e.g. Standard)"
                          className="col-span-4 px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs focus:border-[#A78BFA]/50 outline-none"
                          data-testid={`shipping-rate-name-${r.id}`}
                        />
                        <select
                          value={r.type || 'flat'}
                          onChange={(e) => updateRate(z.id, r.id, { type: e.target.value })}
                          className="col-span-3 px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-[11px] focus:border-[#A78BFA]/50 outline-none"
                          data-testid={`shipping-rate-type-${r.id}`}
                        >
                          <option value="flat">Flat rate</option>
                          <option value="weight_tier">Weight-tiered</option>
                          <option value="price_tier">Price-tiered</option>
                          <option value="carrier">Live carrier rate</option>
                        </select>
                        {(r.type || 'flat') === 'flat' && (
                          <div className="col-span-3 flex items-center gap-1">
                            <span className="text-white/40 text-xs">$</span>
                            <input
                              type="number" min="0" step="0.01"
                              value={r.price}
                              onChange={(e) => updateRate(z.id, r.id, { price: Number(e.target.value) || 0 })}
                              className="flex-1 px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs focus:border-[#A78BFA]/50 outline-none"
                              data-testid={`shipping-rate-price-${r.id}`}
                            />
                          </div>
                        )}
                        {(r.type === 'weight_tier' || r.type === 'price_tier' || r.type === 'carrier') && <div className="col-span-3" />}
                        <div className="col-span-1 flex items-center gap-1 text-[10px] text-white/60">
                          <input type="number" min="0" value={r.min_days} onChange={(e) => updateRate(z.id, r.id, { min_days: Number(e.target.value) || 0 })} className="w-9 px-1 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs focus:border-[#A78BFA]/50 outline-none" data-testid={`shipping-rate-min-${r.id}`} />
                          <span>-</span>
                          <input type="number" min="0" value={r.max_days} onChange={(e) => updateRate(z.id, r.id, { max_days: Number(e.target.value) || 0 })} className="w-9 px-1 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs focus:border-[#A78BFA]/50 outline-none" data-testid={`shipping-rate-max-${r.id}`} />
                        </div>
                        <button
                          onClick={() => deleteRate(z.id, r.id)}
                          className="col-span-1 w-7 h-7 rounded bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center"
                          data-testid={`shipping-rate-delete-${r.id}`}
                        >
                          <Trash2 size={11} className="text-red-400" />
                        </button>
                      </div>

                      {r.type === 'weight_tier' && (
                        <TierEditor
                          tiers={r.tiers || []}
                          upToLabel="Up to (grams)"
                          upToKey="up_to_g"
                          onChange={(nt) => updateRate(z.id, r.id, { tiers: nt })}
                          testidPrefix={`wt-${r.id}`}
                        />
                      )}
                      {r.type === 'price_tier' && (
                        <TierEditor
                          tiers={r.tiers || []}
                          upToLabel="Cart subtotal up to ($)"
                          upToKey="up_to_price"
                          onChange={(nt) => updateRate(z.id, r.id, { tiers: nt })}
                          testidPrefix={`pt-${r.id}`}
                        />
                      )}
                      {r.type === 'carrier' && (
                        <p className="text-[10.5px] text-[#A78BFA] pl-1">
                          Rates are quoted at checkout from any connected carrier (EasyPost / Shippo). Configure the carrier below.
                        </p>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => addRate(z.id)}
                    className="w-full py-2 rounded-lg bg-white/[0.03] border border-dashed border-white/15 text-white/60 hover:bg-white/[0.06] text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5"
                    data-testid={`shipping-add-rate-${z.id}`}
                  >
                    <Plus size={12} /> Add rate to this zone
                  </button>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>

        {/* Carrier accounts */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#A78BFA] flex items-center gap-1.5"><Truck size={11} /> Third-party shipping carriers</p>
          </div>
          <p className="text-[11.5px] text-white/60 mb-3">
            Connect an EasyPost or Shippo account to get live shipping rates at checkout, then buy pre-paid labels straight from your Orders dashboard.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {['easypost', 'shippo'].map((p) => (
              <CarrierCard
                key={p}
                provider={p}
                meta={CARRIER_META[p]}
                config={(config?.carriers || {})[p]}
                onSave={saveCarrier}
                onDelete={disconnectCarrier}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
